import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Center, Flex } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import type { TeamOut } from '@repo/api-codegen';
import { useApiGetTeam } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Icon, IconButton, Text } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useQuestionsEnabled, useSuspenseFeatureAccess } from '../../../api';
import { useTeamEmptyStates } from '../../../api/hooks/team/emptyStates';
import { getSideBarSettingValue } from '../../../api/types/models/team';
import { useSuspenseFeatureAccessCheck } from '../../../utils/authorization/accessMessages';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { Guard } from '../../Guard/Guard';
import { BIDashboardTree } from '../BIDashboardTree';
import { CatalogTreeV2 } from '../CatalogTree/CatalogTreeV2';
import { CatalogTreeV3 } from '../CatalogTree/CatalogTreeV3';
import CollectionsTree from '../CollectionsTree/CollectionsTree';
import { DocumentTree } from '../DocumentTree/DocumentTree';
import { GlossaryTree } from '../GlossaryTree/GlossaryTree';
import { isActive } from '../utils';
import type { Link } from './SideBarLink';
import SideBarLink from './SideBarLink';
import { TeamSettingMenu } from './TeamSettingMenu';
import { useTeamSideBarItemStyle } from './TeamSideBarItem.styles';

export interface TeamSideBarItemHandle {
	open: () => void;
}

interface TeamSideBarItemProps {
	team: TeamOut;
}

export const TeamSideBarItem = forwardRef<
	TeamSideBarItemHandle,
	TeamSideBarItemProps
>(({ team: initialTeamFromList }, ref) => {
	const location = useLocation();
	const { collectionsAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const questionsEnabled = useQuestionsEnabled();

	const { teamsPermissionsV2Frontend, biDashboardsEnabled } = useFeatureFlags();
	const { restrictionMessage: biDashboardsRestrictionMessage } =
		useSuspenseFeatureAccessCheck({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR, UserRole.VIEWER],
			v2Permission: 'Dashboards.Read',
			isFeatureIncludedInPlan: true,
			isAlwaysIncludedFeature: true,
		});

	const showDashboards = biDashboardsEnabled && !biDashboardsRestrictionMessage;

	const [collapsableOpened, setCollapsableOpened] = useLocalStorage({
		key: `team-${initialTeamFromList.id}-sidebar-collapsable-opened`,
		defaultValue: initialTeamFromList.is_default_team,
	});

	const { data: teamDetail } = useApiGetTeam(
		{
			pathParams: {
				teamId: initialTeamFromList.id,
			},
		},
		{
			enabled: !isNil(initialTeamFromList.id),
		}
	);

	useImperativeHandle(ref, () => ({
		open() {
			setCollapsableOpened(true);
		},
	}));

	const { cx, classes, theme } = useTeamSideBarItemStyle({
		open: collapsableOpened,
	});

	const emptyStates = useTeamEmptyStates(initialTeamFromList.id);

	const team = teamDetail || initialTeamFromList;

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: team.id,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		zIndex: isDragging ? 1000 : 'auto',
	};

	const toggleCollapsable = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setCollapsableOpened(!collapsableOpened);
		},
		[collapsableOpened, setCollapsableOpened]
	);

	const contentLinks: Link[] = useMemo(
		() => [
			{
				id: 'nav::home',
				label: 'Home',
				icon: <Icon name="home" color="icon/primary/default" />,
				href: [`/teams/${team.id}/`],
				show:
					getSideBarSettingValue(team.sidebar_settings, 'home') &&
					!!secodaCoreAccess,
				underTeam: true,
			},
			{
				id: 'nav::catalog',
				label: 'Catalog',
				icon: <Icon name="listDetails" color="icon/primary/default" />,
				href: [`/teams/${team.id}/catalog`],
				show: getSideBarSettingValue(team.sidebar_settings, 'catalog'),
				collapsable: true,
				collapsedComponent: teamsPermissionsV2Frontend ? (
					<CatalogTreeV3 teamId={team.id} />
				) : (
					<CatalogTreeV2 open={collapsableOpened} teamId={team.id} />
				),
				count: emptyStates?.data?.exist_dict?.catalog?.is_empty ? 0 : 1,
				underTeam: true,
			},
			{
				id: 'nav::collections',
				label: 'Collections',
				icon: <Icon name="folders" color="icon/primary/default" />,
				href: [`/teams/${team.id}/collections`],
				show:
					getSideBarSettingValue(team.sidebar_settings, 'collections') &&
					!!collectionsAccess,
				collapsable: true,
				collapsedComponent: <CollectionsTree teamId={team.id} />,
				count: emptyStates?.data?.exist_dict?.collection?.is_empty ? 0 : 1,
				underTeam: true,
			},
			{
				id: 'nav::dashboards',
				label: 'Dashboards',
				icon: <Icon name="customDashboard" color="icon/primary/default" />,
				href: [`/teams/${team.id}/bi/dashboards`],
				collapsable: true,
				collapsedComponent: <BIDashboardTree teamId={team.id} />,
				count: emptyStates?.data?.exist_dict?.bi_dashboard?.is_empty ? 0 : 1,
				show:
					getSideBarSettingValue(team.sidebar_settings, 'bi_dashboards') &&
					showDashboards,
				underTeam: true,
			},
			{
				id: 'nav::docs',
				label: 'Documents',
				icon: <Icon name="file" color="icon/primary/default" />,
				href: [`/teams/${team.id}/docs`],
				show: getSideBarSettingValue(team.sidebar_settings, 'documents'),
				collapsable: true,
				collapsedComponent: <DocumentTree teamId={team.id} />,
				count: emptyStates?.data?.exist_dict?.document?.is_empty ? 0 : 1,
				underTeam: true,
			},
			{
				id: 'nav::glossary',
				label: 'Glossary',
				icon: <Icon name="book" color="icon/primary/default" />,
				href: [`/teams/${team.id}/glossary`],
				show: getSideBarSettingValue(team.sidebar_settings, 'glossary'),
				collapsable: true,
				collapsedComponent: <GlossaryTree teamId={team.id} />,
				count: emptyStates?.data?.exist_dict?.glossary?.is_empty ? 0 : 1,
				underTeam: true,
			},

			{
				id: 'nav::questions',
				label: 'Questions',
				icon: (
					<Icon name="messageCircleQuestion" color="icon/primary/default" />
				),
				href: [
					`/teams/${team.id}/questions`,
					`/teams/${team.id}/questions/overview`,
				],
				show:
					getSideBarSettingValue(team.sidebar_settings, 'questions') &&
					questionsEnabled,
				underTeam: true,
			},
		],
		[
			team.id,
			team.sidebar_settings,
			secodaCoreAccess,
			teamsPermissionsV2Frontend,
			collapsableOpened,
			emptyStates?.data?.exist_dict?.catalog?.is_empty,
			emptyStates?.data?.exist_dict?.collection?.is_empty,
			emptyStates?.data?.exist_dict?.glossary?.is_empty,
			emptyStates?.data?.exist_dict?.document?.is_empty,
			emptyStates?.data?.exist_dict?.bi_dashboard?.is_empty,
			collectionsAccess,
			questionsEnabled,
			showDashboards,
		]
	);

	return (
		<>
			<Flex ref={setNodeRef} style={style} className={classes.wrapper}>
				<Flex className={classes.innerWrapper}>
					<Flex
						className={classes.clickArea}
						{...attributes}
						{...listeners}
						onClick={toggleCollapsable}
					>
						<Center w={theme.other.space[7]} h={theme.other.space[7]}>
							<Center
								w={theme.other.space[5]}
								h={theme.other.space[5]}
								p={theme.other.space[0.5]}
							>
								{team.icon}
							</Center>
						</Center>
						<Flex align="center">
							<Text
								size="sm"
								color="text/primary/default"
								weight="semibold"
								lineClamp={1}
							>
								{team.name}
							</Text>
							<Icon name="chevronRight" className={classes.chevron} />
						</Flex>
					</Flex>
					<Guard
						v1AllowedRoles={[UserRole.ADMIN]}
						v2Permission="Teams.Update"
						defaultResult={false}
					>
						<TeamSettingMenu team={team} position="right-start" withinPortal>
							<IconButton
								iconName="dots"
								variant="tertiary"
								size="sm"
								className={cx(classes.hiddenButton, classes.displayOnHover)}
								tooltip="Show actions"
								tooltipProps={{ position: 'right', withArrow: true }}
								mr="xs"
							/>
						</TeamSettingMenu>
					</Guard>
				</Flex>
			</Flex>

			<Flex
				w="100%"
				direction="column"
				pb="xs"
				sx={{
					display: collapsableOpened ? 'flex' : 'none',
				}}
			>
				{contentLinks
					.filter((link) => link.show)
					.map((link) => (
						<SideBarLink
							key={link.id}
							link={link}
							selected={isActive(link.href, location, true)}
						/>
					))}
			</Flex>
		</>
	);
});

TeamSideBarItem.displayName = 'TeamSideBarItem';
